import { Box, CardContent, TextField, Button, Divider } from "@mui/material";
import { useState, useRef } from "react";
import { useVerify2FA } from "../../../../../../queries/2fa/verify2FA";
import {
  DEFAULT_BG_COLOR,
  DEFAULT_KEY_LABEL_COLOR,
  DEFAULT_LABEL_COLOR,
  getErrorSnackbar,
  getXtenantId,
} from "../../../../../../utils/utils";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import SuccessModal, {
  useSuccess,
} from "../../../../../../components/SuccessModal";
import { PROVIDER_PATHS } from "../../../../../../constants/paths";
import { useNavigate } from "react-router-dom";
import OutlineCard from "../../../../../../components/OutlineCard";
import Text from "../../../../../../components/Text";
import useCurrentUser from "../../../../../../hooks/local-storage/use-current-user";

// Interface for the API data
interface TwoFASetupData {
  authType: string;
  message: string | null;
  secretKey: string;
  qrCodeUrl: string;
  qrCodeBase64: string;
  backupCodes: string[] | null;
}

interface GoogleAuthenticatorProps {
  apiData: TwoFASetupData | null;
}

const GoogleAuthenticator = ({ apiData }: GoogleAuthenticatorProps) => {
  const navigate = useNavigate();
  const [openedSuccess, { open: openSuccess, close: closeSuccess }] =
    useSuccess(() => {
      navigate(PROVIDER_PATHS.DASHBOARD);
    });

  const { mutateAsync: verify2FA } = useVerify2FA();
  const showSnackbar = useSnackbar();
  const [me] = useCurrentUser();
  const [verificationCode, setVerificationCode] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [step, setStep] = useState(1);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleNext = () => {
    // console.log("Moving to verification step");
    setStep(2);
  };

  const handleVerify = () => {
    const codeString = verificationCode.join("");
    // console.log("Verifying Google Authenticator code:", codeString);
    // Add verification logic here
    verify2FA({
      verify2FARequest: {
        code: codeString,
        email: me?.email ?? "",
        login: false,
      },
      xTENANTID: getXtenantId() ?? undefined,
    })
      .then((res) => {
        // console.log("res verify2FA", res);
        if (res.data.code === "TWO_FACTOR_AUTH_GOOGLE_INVALID") {
          showSnackbar({
            message: (res?.data?.message as any) || "Invalid code",
            type: "error",
          });
        } else if (res.data.code === "TWO_FACTOR_AUTH_GOOGLE_VERIFIED") {
          // showSnackbar({ message: res?.data?.message as any || "Code verified successfully", type: "success" });
          openSuccess();
        }
      })
      .catch((err) => {
        // console.log("err verify2FA", err);
        showSnackbar(getErrorSnackbar(err));
      });
    setVerificationCode(["", "", "", "", "", ""]);
  };

  const handleBack = () => {
    setStep(1);
    setVerificationCode(["", "", "", "", "", ""]);
  };

  const handleOtpChange = (index: number, value: string) => {
    // Only allow single digit and numbers
    if (value.length > 1) return;

    // Check if the value is a number (0-9)
    if (value && !/^[0-9]$/.test(value)) return;

    const newOtp = [...verificationCode];
    newOtp[index] = value;
    setVerificationCode(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiData?.secretKey || "");
    // console.log("Secret key copied to clipboard");
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 2,
      }}
    >
      <SuccessModal
        label="Google Authenticator setup completed successfully."
        opened={openedSuccess}
        onClose={closeSuccess}
      />
      <OutlineCard
        sx={{
          width: "100%",
          maxWidth: 700,
          backgroundColor: DEFAULT_BG_COLOR,
        }}
      >
        <CardContent sx={{ padding: 5, textAlign: "center" }}>
          <Text
            sx={{
              fontWeight: 400,
              fontSize: 16,
              color: DEFAULT_LABEL_COLOR,
              marginBottom: 2,
            }}
          >
            Google Authenticator Setup
          </Text>

          <Text
            sx={{
              fontSize: 14,
              color: DEFAULT_KEY_LABEL_COLOR,
              marginBottom: 4,
              lineHeight: 1.6,
            }}
          >
            Set up app-based two-factor authentication using Google
            Authenticator.
          </Text>

          <OutlineCard
            sx={{
              padding: 3,
              backgroundColor: "#FFFFFF",
              width: "100%",
            }}
          >
            {step === 1 ? (
              <>
                <Text
                  sx={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: DEFAULT_KEY_LABEL_COLOR,
                    marginBottom: 3,
                    textAlign: "left",
                  }}
                >
                  Step 1: Add your account to Google Authenticator
                </Text>

                <Box sx={{ marginBottom: 3 }}>
                  <Text
                    sx={{
                      fontSize: 14,
                      color: DEFAULT_KEY_LABEL_COLOR,
                      marginBottom: 2,
                      textAlign: "left",
                    }}
                  >
                    1. Open Google Authenticator on your phone
                  </Text>
                  {apiData?.qrCodeBase64 ? (
                    <Text
                      sx={{
                        color: DEFAULT_KEY_LABEL_COLOR,
                        marginBottom: 2,
                        textAlign: "left",
                      }}
                    >
                      2. Tap the + button and scan the QR code below
                    </Text>
                  ) : (
                    <Text
                      sx={{
                        color: DEFAULT_KEY_LABEL_COLOR,
                        marginBottom: 2,
                        textAlign: "left",
                      }}
                    >
                      2. Tap the + button and manually enter the secret key
                      below
                    </Text>
                  )}
                  <Text
                    sx={{
                      color: DEFAULT_KEY_LABEL_COLOR,
                      marginBottom: 2,
                      textAlign: "left",
                    }}
                  >
                    3. Or manually enter the secret key
                  </Text>
                </Box>

                {apiData?.qrCodeBase64 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: 3,
                    }}
                  >
                    <img
                      src={`data:image/png;base64,${apiData.qrCodeBase64}`}
                      alt="QR Code"
                      style={{
                        width: 200,
                        height: 200,
                        border: "1px solid #E7E7E7",
                        borderRadius: 8,
                      }}
                    />
                  </Box>
                )}

                <Divider sx={{ marginBottom: 3 }} />

                <Text
                  sx={{
                    fontWeight: 500,
                    color: DEFAULT_KEY_LABEL_COLOR,
                    marginBottom: 2,
                    textAlign: "left",
                  }}
                >
                  Manual Entry Secret Key:
                </Text>

                <Box sx={{ display: "flex", gap: 1, marginBottom: 3 }}>
                  <TextField
                    fullWidth
                    value={apiData?.secretKey || ""}
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#B4B5B5",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: "12px 16px",
                        fontSize: 14,
                        fontFamily: "monospace",
                        textAlign: "center",
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={copyToClipboard}
                    sx={{
                      minWidth: "auto",
                      borderColor: DEFAULT_KEY_LABEL_COLOR,
                      color: DEFAULT_LABEL_COLOR,
                      textTransform: "none",
                    }}
                  >
                    Copy
                  </Button>
                </Box>

                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{
                    width: "100%",
                    backgroundColor: "primary.main",
                  }}
                >
                  Next: Verify Setup
                </Button>
              </>
            ) : (
              <>
                <Text
                  sx={{
                    color: DEFAULT_KEY_LABEL_COLOR,
                    marginBottom: 2,
                    textAlign: "left",
                  }}
                >
                  Step 2: Verify your setup
                </Text>

                <Text
                  sx={{
                    color: DEFAULT_KEY_LABEL_COLOR,
                    marginBottom: 3,
                    textAlign: "left",
                  }}
                >
                  Enter the 6-digit code from your Google Authenticator app to
                  verify the setup.
                </Text>

                <Box sx={{ marginBottom: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      width: "100%",
                      marginBottom: 2,
                    }}
                  >
                    {verificationCode.map((digit, index) => (
                      <TextField
                        key={index}
                        inputRef={(el) => (inputRefs.current[index] = el)}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        inputProps={{
                          maxLength: 1,
                          style: {
                            textAlign: "center",
                            fontSize: 16,
                            fontWeight: 400,
                          },
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                        sx={{
                          flex: 1,
                          "& .MuiOutlinedInput-root": {
                            height: 48,
                            "& fieldset": {
                              borderColor: "#B4B5B5",
                            },
                            "&:hover fieldset": {
                              borderColor: "#373D41",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#104E84",
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                <Box
                  sx={{ display: "flex", gap: 2, justifyContent: "stretch" }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{
                      flex: 1,
                      color: DEFAULT_KEY_LABEL_COLOR,
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleVerify}
                    disabled={verificationCode.join("").length !== 6}
                    sx={{
                      flex: 1,
                      backgroundColor: "primary.main",
                      textTransform: "none",
                    }}
                  >
                    Verify & Enable
                  </Button>
                </Box>
              </>
            )}
          </OutlineCard>
        </CardContent>
      </OutlineCard>
    </Box>
  );
};

export default GoogleAuthenticator;
